import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { useComponentScope } from 'features/libra/workspace/ComponentScopeContext'
import { useCallback, useMemo } from 'react'

import {
  type FlattenedOntologyDefinition,
  flattenWorkflowOntologyFields,
} from './flattenOntologyFields'
import {
  omitComponentSubtree,
  OntologyField,
  readComponentSubtree,
} from './ontologyFieldTypes'

/**
 * Pure form of {@link useTicketOntologyFields}: the TICKET-scoped ontology
 * fields of a raw `globalContext.ontologyFields` value — its top-level
 * entries with the reserved `components` key stripped. Use for a workflow
 * that isn't (or isn't only) the one mounted in `WorkflowContext` — e.g.
 * resolving ontology fields for several tickets' workflows at once.
 */
export function ticketOntologyFieldsOf(
  ontologyFields: unknown
): Record<string, OntologyField> {
  if (!ontologyFields || typeof ontologyFields !== 'object') return {}
  return omitComponentSubtree(
    ontologyFields as Record<string, unknown>
  ) as Record<string, OntologyField>
}

/**
 * Returns the workflow's TICKET-scoped ontology fields — top-level entries of
 * `workflow.globalContext.ontologyFields` with the reserved `components` key stripped.
 *
 * Use in top-level form renderers (outside `UnitComponentTabs`), NII screening,
 * document extractors, etc. — anywhere that reads a flat
 * `Record<fieldId, OntologyField>` and only cares about ticket-global fields.
 */
export function useTicketOntologyFields(): {
  ontologyFields: Record<string, OntologyField>
  getField: (fieldId: string) => OntologyField | undefined
} {
  const workflow = useWorkflow()

  const ontologyFields = useMemo(
    () => ticketOntologyFieldsOf(workflow.globalContext.ontologyFields),
    [workflow.globalContext.ontologyFields]
  )

  const getField = useCallback(
    (fieldId: string): OntologyField | undefined => ontologyFields[fieldId],
    [ontologyFields]
  )

  return useMemo(
    () => ({ ontologyFields, getField }),
    [ontologyFields, getField]
  )
}

/**
 * Pure form of {@link useComponentOntologyFields}: the COMPONENT-scoped
 * ontology definitions of a raw `globalContext.ontologyFields` value, empty
 * when none are declared.
 */
export function componentOntologyFieldsOf(
  ontologyFields: unknown
): Record<string, OntologyField> {
  if (!ontologyFields || typeof ontologyFields !== 'object') return {}
  const components = readComponentSubtree(
    ontologyFields as Record<string, unknown>
  )
  if (!components || typeof components !== 'object') return {}
  return components as Record<string, OntologyField>
}

/**
 * The "this workflow is asset/component-scoped" marker: true when the
 * workflow declares any per-component ontology fields. Pure so non-hook
 * callers (e.g. workflow-scoped provider `appliesTo` predicates) can share
 * the exact signal `useComponentOntologyFields` consumers key on.
 */
export function workflowHasComponentOntology(workflow: LibraWorkflow): boolean {
  return (
    Object.keys(
      componentOntologyFieldsOf(workflow.globalContext.ontologyFields)
    ).length > 0
  )
}

/**
 * Returns the workflow's COMPONENT-scoped ontology fields — `ontologyFields.components`
 * as `Record<fieldId, OntologyField>`. Empty when the workflow declares no per-component
 * fields.
 *
 * A non-empty result is the new "this workflow is component-scoped" signal — it replaces
 * the deprecated `requiresComponentScopes` flag on `globalContext`.
 *
 * Use inside `UnitComponentTabs` slots so per-component form renderers get exactly the
 * definitions they should render for the active component tab.
 */
export function useComponentOntologyFields(): {
  ontologyFields: Record<string, OntologyField>
  getField: (fieldId: string) => OntologyField | undefined
} {
  const workflow = useWorkflow()

  const ontologyFields = useMemo(
    () => componentOntologyFieldsOf(workflow.globalContext.ontologyFields),
    [workflow.globalContext.ontologyFields]
  )

  const getField = useCallback(
    (fieldId: string): OntologyField | undefined => ontologyFields[fieldId],
    [ontologyFields]
  )

  return useMemo(
    () => ({ ontologyFields, getField }),
    [ontologyFields, getField]
  )
}

/**
 * Returns a flattened list of every ontology definition across BOTH scopes. Each entry
 * carries a `scope: 'ticket' | 'component'` tag so callers can distinguish the two
 * without walking the nested shape themselves.
 *
 * Use in single-workflow contexts (inside a `WorkflowProvider`) — for LLM diff drawers,
 * form renderers that legitimately span both scopes, etc. For MULTI-workflow contexts
 * (e.g., the dashboard LLM stats tab, which fans out across many workflows), call the
 * pure helper {@link flattenWorkflowOntologyFields} directly with a manually-built
 * ontology map.
 */
export function useFlattenedOntologyFields(): FlattenedOntologyDefinition[] {
  const workflow = useWorkflow()

  return useMemo(
    () => flattenWorkflowOntologyFields(workflow.globalContext.ontologyFields),
    [workflow.globalContext.ontologyFields]
  )
}

/**
 * The ontology definitions for the ACTIVE scope: component-scoped when a
 * `ComponentScopeContext` is mounted (inside `UnitComponentTabs`), else the
 * ticket-scoped top level. One call replaces the pick-by-scope ternary that
 * consumers used to re-implement (both hooks are still called
 * unconditionally, satisfying rules-of-hooks).
 */
export function useActiveScopeOntologyFields(): {
  ontologyFields: Record<string, OntologyField>
  getField: (fieldId: string) => OntologyField | undefined
} {
  const componentScope = useComponentScope()
  const componentFields = useComponentOntologyFields()
  const ticketFields = useTicketOntologyFields()
  return componentScope ? componentFields : ticketFields
}
